import { useMemo } from "react";
import { Link } from "react-router";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/shared/empty-state";
import { CardSkeleton } from "@/components/shared/loading-skeleton";
import { ROUTES } from "@/app/routes";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import {
  postApiAiProviderConnectionsModelCatalog,
  useGetApiAiProviderConnections,
} from "@/lib/api/generated/providers/providers";
import type {
  TenantAiProviderConnectionRecordDto,
  TenantAiProviderModelCatalogEntryDto,
  TenantAiProviderModelCatalogResponseDto,
} from "@/lib/api/generated/control-api.schemas";
import { groupAiProviderMetadata } from "./provider-metadata.js";
import { OpenAICodexConnectCard } from "./openai-codex-connect-card.js";
import { ProviderConnectionCard } from "./provider-connection-card.js";

export function AiProvidersPage() {
  const { tenantId: activeTenantId } = useTenantContext();
  const { data: allConnectionsData, isLoading: connectionsLoading } = useGetApiAiProviderConnections({
    query: { enabled: Boolean(activeTenantId) },
  });
  const allConnections = (allConnectionsData ?? []) as TenantAiProviderConnectionRecordDto[];
  const { data: catalog, isLoading: catalogLoading } = useQuery({
    queryKey: ["tenants", activeTenantId, "modelCatalog", "all"],
    queryFn: () =>
      postApiAiProviderConnectionsModelCatalog({ providerId: null }) as Promise<TenantAiProviderModelCatalogResponseDto>,
    enabled: Boolean(activeTenantId),
  });
  useDocumentTitle("AI Providers");
  const isLoading = connectionsLoading || catalogLoading;

  const connections = useMemo(
    () =>
      allConnections.filter(
        (record) => {
          const metadata =
            record.metadata && typeof record.metadata === "object" && !Array.isArray(record.metadata)
              ? (record.metadata as Record<string, unknown>)
              : null;
          const capabilityType =
            typeof metadata?.capabilityType === "string" ? metadata.capabilityType.toLowerCase() : "ai_model";
          return capabilityType !== "web_search";
        },
      ),
    [allConnections],
  );

  const providerGroups = useMemo(() => groupAiProviderMetadata(), []);
  const connectionsByProvider = useMemo(() => {
    const map = new Map<string, TenantAiProviderConnectionRecordDto>();
    for (const connection of connections) {
      if (!map.has(connection.providerId)) {
        map.set(connection.providerId, connection);
      }
    }
    return map;
  }, [connections]);
  const modelCountsByProvider = useMemo(() => {
    const counts = new Map<string, number>();
    const connectionById = new Map(
      (catalog?.connections ?? []).map((connection) => [connection.id, connection] as const),
    );
    for (const model of catalog?.models ?? []) {
      const ownerProviderId = resolveCatalogOwnerProviderId(model, connectionById);
      counts.set(ownerProviderId, (counts.get(ownerProviderId) ?? 0) + 1);
    }
    return counts;
  }, [catalog]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader className="gap-2">
            <CardTitle>AI Providers</CardTitle>
            <CardDescription>Loading tenant provider connections.</CardDescription>
          </CardHeader>
        </Card>
        <div className="grid gap-3 md:grid-cols-2">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      </div>
    );
  }

  if (!activeTenantId) {
    return <EmptyState title="Missing tenant" description="Open this page inside a tenant workspace." />;
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader className="gap-2">
          <CardTitle>AI Providers</CardTitle>
          <CardDescription>
            Connect a tenant-shared model provider once, validate it, and make it available to
            conversations, agents, and automations.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2 text-xs" style={{ color: "var(--muted-foreground)" }}>
          <span>{connections.length} configured connection{connections.length === 1 ? "" : "s"}</span>
          <span>·</span>
          <span>{(catalog?.models ?? []).length} catalog entries</span>
          <Button asChild size="sm" variant="outline">
            <Link to={ROUTES.tenantAutomations(activeTenantId)}>Automations</Link>
          </Button>
        </CardContent>
      </Card>

      {[...providerGroups.entries()].map(([family, providers]) => (
        <section key={family} className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
                {family}
              </h2>
              <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                Tenant-shared provider connections for this family.
              </p>
            </div>
            <Badge variant="outline">{providers.length} provider{providers.length === 1 ? "" : "s"}</Badge>
          </div>
          <div className="grid gap-4 xl:grid-cols-2">
            {providers.map((metadata) => {
              const existing = connectionsByProvider.get(metadata.providerId) ?? null;
              const sharedProps = {
                tenantId: activeTenantId,
                metadata,
                existing,
                modelCount: modelCountsByProvider.get(metadata.providerId) ?? 0,
              };
              const providerCardKey = [
                metadata.providerId,
                existing?.id ?? "new",
                existing?.status ?? "disconnected",
                existing?.lastValidatedAt ?? "never",
                existing?.profileId ?? "default",
              ].join(":");
              if (metadata.providerId === "openai-codex") {
                return <OpenAICodexConnectCard key={providerCardKey} {...sharedProps} />;
              }
              return (
                <ProviderConnectionCard key={providerCardKey} {...sharedProps} />
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function resolveCatalogOwnerProviderId(
  model: TenantAiProviderModelCatalogEntryDto,
  connectionById: Map<string, TenantAiProviderConnectionRecordDto>,
): string {
  const connectionId = model.connectionId?.trim();
  if (connectionId) {
    const connection = connectionById.get(connectionId);
    if (connection?.providerId?.trim()) {
      return connection.providerId.trim().toLowerCase();
    }
  }

  const providerGroupId = model.providerGroupId?.trim();
  if (providerGroupId) {
    return providerGroupId.toLowerCase();
  }

  return model.provider.trim().toLowerCase();
}
